/**
 * Throttle utility
 * Limits the rate at which a function can be called
 */

export interface ThrottledFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
}

/**
 * Creates a throttled function that only invokes the provided function at most once per every `delay` milliseconds.
 * 
 * @param func The function to throttle
 * @param delay The number of milliseconds to throttle invocations to
 * @param options Throttle options
 * @returns The throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): ThrottledFunction<T> {
  const { leading = true, trailing = true } = options;
  
  let timeout: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: any = null;
  let lastCallTime: number | null = null;
  let result: any;

  function invokeFunc(time: number) {
    const args = lastArgs;
    const thisArg = lastThis;
    
    lastArgs = null;
    lastThis = null;
    lastCallTime = time;
    
    result = func.apply(thisArg, args!);
    return result;
  }

  function startTimer(pendingFunc: () => void, wait: number) {
    return setTimeout(pendingFunc, wait);
  }

  function cancelTimer() {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  }

  function leadingEdge(time: number) {
    // Reset any `timeout` since we're starting a new throttle period
    lastCallTime = time;
    // Start the timer for the trailing edge
    timeout = startTimer(timerExpired, delay);
    // Invoke the leading edge callback
    return leading ? invokeFunc(time) : result;
  }

  function timerExpired() {
    const time = Date.now();
    
    // Restart the timer if we still have pending calls
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    
    // Otherwise, reset the timeout
    timeout = null;
  }

  function trailingEdge(time: number) {
    timeout = null;
    
    // Only invoke if we have `lastArgs` which means `func` has been called during the throttle period
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    
    lastArgs = null;
    lastThis = null;
    return result;
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = lastCallTime === null ? 0 : time - lastCallTime;
    
    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, enough time has passed since the last call
    return (
      lastCallTime === null ||
      timeSinceLastCall >= delay ||
      timeSinceLastCall < 0
    );
  }

  function throttled(this: any, ...args: Parameters<T>): void {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);
    
    lastArgs = args;
    lastThis = this;
    
    if (isInvoking) {
      if (timeout === null) {
        return leadingEdge(time);
      }
    }
    
    if (timeout === null) {
      timeout = startTimer(timerExpired, delay);
    }
  }

  throttled.cancel = function() {
    cancelTimer();
    lastArgs = null;
    lastThis = null;
    lastCallTime = null;
  };

  throttled.flush = function() {
    return timeout === null ? result : trailingEdge(Date.now());
  };

  return throttled;
}

/**
 * Simple throttle implementation for basic use cases
 */
export function simpleThrottle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  
  return function(...args: Parameters<T>) {
    const now = Date.now();
    
    if (now - lastCall >= delay) {
      lastCall = now;
      return func(...args);
    }
  };
}